import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  ArrowLeft, Shuffle, Building2, Briefcase, Sparkles, MapPin, Filter,
  ChevronDown, ChevronUp, RotateCw, Trophy, Globe, Send,
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const SDG_LABELS = {
  1: 'No Poverty', 2: 'Zero Hunger', 3: 'Good Health', 4: 'Quality Education',
  5: 'Gender Equality', 6: 'Clean Water', 7: 'Affordable Energy', 8: 'Decent Work',
  9: 'Industry & Innovation', 10: 'Reduced Inequalities', 11: 'Sustainable Cities',
  12: 'Responsible Consumption', 13: 'Climate Action', 14: 'Life Below Water',
  15: 'Life on Land', 16: 'Peace & Justice', 17: 'Partnerships',
};

const QUALITY_STYLES = {
  'Excellent Match': 'bg-emerald-100 text-emerald-800 border-emerald-200',
  'Good Match':      'bg-sky-100 text-sky-800 border-sky-200',
  'Moderate Match':  'bg-amber-100 text-amber-800 border-amber-200',
  'Weak Match':      'bg-orange-100 text-orange-800 border-orange-200',
  'Poor Match':      'bg-rose-100 text-rose-800 border-rose-200',
};

const SECTOR_OPTIONS = [
  { value: 'any', label: 'Any sector' },
  { value: 'social_enterprise', label: 'Social Enterprise' },
  { value: 'non_profit', label: 'Non-Profit' },
  { value: 'cooperative', label: 'Cooperative' },
  { value: 'ngo', label: 'NGO' },
  { value: 'education', label: 'Education' },
  { value: 'environment', label: 'Environment' },
  { value: 'health', label: 'Health' },
];

// Compact 6-dimension breakdown bar — used inside expanded match card.
const ScoreBars = ({ breakdown }) => {
  const order = [
    ['sdg_alignment',      'SDG'],
    ['geographic_match',   'Geo'],
    ['sector_match',       'Sector'],
    ['investment_size_match', 'Size'],
    ['esg_alignment',      'ESG'],
    ['non_financial_match','Non-fin'],
  ];
  return (
    <div className="grid grid-cols-3 gap-2 text-xs">
      {order.map(([k, label]) => {
        const dim = breakdown?.[k] || { score: 0, max: 0 };
        const pct = dim.max ? Math.round((dim.score / dim.max) * 100) : 0;
        return (
          <div key={k} data-testid={`bar-${k}`}>
            <div className="flex justify-between text-slate-600 mb-0.5">
              <span>{label}</span>
              <span className="font-mono">{dim.score}/{dim.max}</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded">
              <div
                className="h-full rounded bg-teal-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MatchCard = ({ m, onContact, onExpand, expanded }) => {
  const qStyle = QUALITY_STYLES[m.match_quality] || 'bg-slate-100 text-slate-700';
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`match-card-${m.actor_id}-${m.investor_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`${qStyle} border text-xs`} data-testid="match-quality-badge">
                {m.match_quality}
              </Badge>
              <span className="text-2xl font-bold text-slate-900" data-testid="match-score">
                {m.match_score}
              </span>
              <span className="text-xs text-slate-500">/100</span>
              {m.distance_km !== null && m.distance_km !== undefined && (
                <span className="inline-flex items-center text-xs text-slate-500 ml-1">
                  <MapPin className="h-3 w-3 mr-0.5" /> {m.distance_km} km
                </span>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div className="bg-blue-50/60 rounded p-2">
                <p className="text-xs uppercase tracking-wide text-blue-700 font-semibold flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Social Actor
                </p>
                <p className="font-medium text-slate-900 truncate" title={m.actor_name}>{m.actor_name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[m.actor_type, m.actor_city, m.actor_country].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
              <div className="bg-emerald-50/60 rounded p-2">
                <p className="text-xs uppercase tracking-wide text-emerald-700 font-semibold flex items-center gap-1">
                  <Briefcase className="h-3 w-3" /> Investor
                </p>
                <p className="font-medium text-slate-900 truncate" title={m.investor_name}>{m.investor_name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {[m.investor_type, m.investor_city, m.investor_country].filter(Boolean).join(' · ') || '—'}
                </p>
              </div>
            </div>

            {m.matching_sdgs?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                <span className="text-xs text-slate-500 mr-1">Common SDGs:</span>
                {m.matching_sdgs.map(s => (
                  <Badge key={s} variant="outline" className="text-xs">
                    SDG&nbsp;{s}: {SDG_LABELS[s] || ''}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onExpand(m)}
              data-testid="expand-match-btn"
            >
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
            <Button
              size="sm"
              onClick={() => onContact(m)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="open-actor-btn"
            >
              <Send className="h-3 w-3 mr-1" /> Open Actor
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-3 border-t" data-testid="match-breakdown">
            <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Score breakdown</p>
            <ScoreBars breakdown={m.score_breakdown} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SupplyDemandMatching = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [topLoading, setTopLoading] = useState(true);

  // Filter state
  const [selectedSdg, setSelectedSdg] = useState([]);
  const [sector, setSector] = useState('any');
  const [minBudget, setMinBudget] = useState('');
  const [maxBudget, setMaxBudget] = useState('');
  const [maxDistance, setMaxDistance] = useState('');
  const [minScore, setMinScore] = useState([20]);

  // Results
  const [matches, setMatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [topMatches, setTopMatches] = useState([]);
  const [topCounts, setTopCounts] = useState({ actor_count: 0, investor_count: 0 });
  const [expandedKey, setExpandedKey] = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const toggleSdg = (sdg) => {
    setSelectedSdg(prev => prev.includes(sdg) ? prev.filter(x => x !== sdg) : [...prev, sdg]);
  };

  const resetFilters = () => {
    setSelectedSdg([]);
    setSector('any');
    setMinBudget('');
    setMaxBudget('');
    setMaxDistance('');
    setMinScore([20]);
  };

  const runAdvancedMatch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      selectedSdg.forEach(s => params.append('sdg', String(s)));
      if (sector && sector !== 'any') params.append('sector', sector);
      if (minBudget) params.append('min_budget', minBudget);
      if (maxBudget) params.append('max_budget', maxBudget);
      if (maxDistance) params.append('max_distance_km', maxDistance);
      params.append('min_score', String(minScore[0] || 0));
      params.append('limit', '50');

      const res = await axios.get(`${API}/api/matching/advanced?${params}`, { headers });
      setMatches(res.data.matches || []);
      setStats(res.data.stats || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Matching failed');
    } finally {
      setLoading(false);
    }
  }, [selectedSdg, sector, minBudget, maxBudget, maxDistance, minScore, headers]);

  const fetchTopMatches = useCallback(async () => {
    setTopLoading(true);
    try {
      const res = await axios.get(`${API}/api/matching/top-matches?limit=10`, { headers });
      setTopMatches(res.data.top_matches || []);
      setTopCounts({
        actor_count: res.data.actor_count || 0,
        investor_count: res.data.investor_count || 0,
      });
    } catch {
      // silent
    } finally {
      setTopLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchTopMatches();
  }, [fetchTopMatches]);

  // Auto-run when user switches to the Advanced tab the first time.
  useEffect(() => {
    if (tab === 'advanced' && matches.length === 0 && !loading) {
      runAdvancedMatch();
    }
  }, [tab]);

  const handleContact = (m) => {
    // Open the social actor's public profile so the viewer can decide how to engage.
    navigate(`/social-actor-registry?open=${m.actor_id}`);
  };

  const handleExpand = (m) => {
    const k = `${m.actor_id}-${m.investor_id}`;
    setExpandedKey(prev => prev === k ? null : k);
  };

  const SDG_GRID = Array.from({ length: 17 }, (_, i) => i + 1);

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-4">
        <Button
          data-testid="back-to-dashboard-btn"
          variant="outline"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Shuffle className="h-7 w-7 text-teal-700" /> Supply &amp; Demand Matching
          </h1>
          <p className="text-slate-600 mt-1">
            Connect Social Actors with Investors via SDG alignment, geographic proximity, and investment criteria.
          </p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Trophy className="h-4 w-4 mr-1" /> Overview
          </TabsTrigger>
          <TabsTrigger value="advanced" data-testid="tab-advanced">
            <Sparkles className="h-4 w-4 mr-1" /> Advanced Matching
          </TabsTrigger>
        </TabsList>

        {/* ─── Overview tab ─────────────────────────────────────────── */}
        <TabsContent value="overview" className="space-y-6 mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card><CardContent className="p-4 text-center" data-testid="stat-actors">
              <Building2 className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <p className="text-3xl font-bold text-blue-700">{topCounts.actor_count}</p>
              <p className="text-sm text-slate-600">Social Actors (Supply)</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center" data-testid="stat-pairs">
              <Shuffle className="h-8 w-8 text-teal-700 mx-auto mb-2" />
              <p className="text-3xl font-bold text-teal-700">{topMatches.length}</p>
              <p className="text-sm text-slate-600">Top mutual matches</p>
            </CardContent></Card>
            <Card><CardContent className="p-4 text-center" data-testid="stat-investors">
              <Briefcase className="h-8 w-8 text-emerald-700 mx-auto mb-2" />
              <p className="text-3xl font-bold text-emerald-700">{topCounts.investor_count}</p>
              <p className="text-sm text-slate-600">Social Investors (Demand)</p>
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Trophy className="h-4 w-4 text-amber-600" /> Top mutual matches
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {topLoading ? (
                <div className="text-center py-8 text-slate-500">Computing matches...</div>
              ) : topMatches.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  Not enough approved actors/investors to compute matches yet.
                </div>
              ) : (
                topMatches.map((m, idx) => (
                  <div
                    key={`${m.actor_id}-${m.investor_id}`}
                    className="flex items-center gap-3 p-3 bg-slate-50 rounded"
                    data-testid={`top-match-row-${idx}`}
                  >
                    <span className="text-2xl font-bold text-amber-600 w-8 text-center">{idx + 1}</span>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2 min-w-0">
                      <div className="truncate">
                        <Building2 className="h-3 w-3 text-blue-600 inline mr-1" />
                        <span className="font-medium text-sm">{m.actor_name}</span>
                        {m.actor_city && <span className="text-xs text-slate-500"> · {m.actor_city}</span>}
                      </div>
                      <div className="truncate">
                        <Briefcase className="h-3 w-3 text-emerald-700 inline mr-1" />
                        <span className="font-medium text-sm">{m.investor_name}</span>
                        {m.investor_city && <span className="text-xs text-slate-500"> · {m.investor_city}</span>}
                      </div>
                    </div>
                    <Badge className={`${QUALITY_STYLES[m.match_quality]} border text-xs`}>
                      {m.match_score}
                    </Badge>
                    {m.distance_km !== null && m.distance_km !== undefined && (
                      <span className="text-xs text-slate-500 inline-flex items-center">
                        <Globe className="h-3 w-3 mr-0.5" /> {m.distance_km} km
                      </span>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Advanced Matching tab ─────────────────────────────────── */}
        <TabsContent value="advanced" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            {/* Filter panel */}
            <Card className="lg:col-span-4 xl:col-span-3 h-fit lg:sticky lg:top-4">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Filter className="h-4 w-4 text-teal-700" /> Filters
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs">SDG focus</Label>
                  <div className="grid grid-cols-3 gap-1 mt-1">
                    {SDG_GRID.map(n => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => toggleSdg(n)}
                        className={`text-xs rounded px-1.5 py-1 border transition ${
                          selectedSdg.includes(n)
                            ? 'bg-teal-600 text-white border-teal-700'
                            : 'bg-white text-slate-700 border-slate-200 hover:border-teal-300'
                        }`}
                        title={SDG_LABELS[n]}
                        data-testid={`sdg-pill-${n}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {selectedSdg.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">
                      {selectedSdg.length} SDG{selectedSdg.length > 1 ? 's' : ''} selected
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs">Sector / type</Label>
                  <Select value={sector} onValueChange={setSector}>
                    <SelectTrigger className="mt-1" data-testid="sector-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SECTOR_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Min budget (€)</Label>
                    <Input
                      type="number" placeholder="0" value={minBudget}
                      onChange={e => setMinBudget(e.target.value)}
                      data-testid="min-budget-input" className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Max budget (€)</Label>
                    <Input
                      type="number" placeholder="∞" value={maxBudget}
                      onChange={e => setMaxBudget(e.target.value)}
                      data-testid="max-budget-input" className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> Max distance (km)
                  </Label>
                  <Input
                    type="number" placeholder="Any" value={maxDistance}
                    onChange={e => setMaxDistance(e.target.value)}
                    data-testid="max-distance-input" className="mt-1"
                  />
                </div>

                <div>
                  <Label className="text-xs flex justify-between">
                    <span>Min score</span>
                    <span className="font-mono text-teal-700">{minScore[0]}</span>
                  </Label>
                  <Slider
                    value={minScore} onValueChange={setMinScore}
                    min={0} max={100} step={5}
                    className="mt-2" data-testid="min-score-slider"
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={runAdvancedMatch}
                    disabled={loading}
                    className="flex-1 bg-teal-600 hover:bg-teal-700"
                    data-testid="run-match-btn"
                  >
                    {loading ? 'Matching...' : 'Apply Filters'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetFilters}
                    data-testid="reset-filters-btn"
                  >
                    <RotateCw className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Results panel */}
            <div className="lg:col-span-8 xl:col-span-9 space-y-3">
              {stats && (
                <Card data-testid="match-stats">
                  <CardContent className="p-4 flex items-center gap-6 flex-wrap">
                    <div>
                      <p className="text-xs uppercase text-slate-500">Matches</p>
                      <p className="text-2xl font-bold text-teal-700">{stats.total}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-slate-500">Avg score</p>
                      <p className="text-2xl font-bold text-slate-900">{stats.avg_score}</p>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {Object.entries(stats.by_quality)
                        .filter(([, v]) => v > 0)
                        .map(([q, v]) => (
                          <Badge key={q} className={`${QUALITY_STYLES[q]} border text-xs`}>
                            {q}: {v}
                          </Badge>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {loading ? (
                <Card><CardContent className="p-12 text-center text-slate-500">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-3"></div>
                  Computing matches...
                </CardContent></Card>
              ) : matches.length === 0 ? (
                <Card data-testid="no-matches">
                  <CardContent className="p-12 text-center text-slate-500">
                    <Sparkles className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="font-medium">No matches found</p>
                    <p className="text-sm mt-1">Try lowering the minimum score or removing filters.</p>
                  </CardContent>
                </Card>
              ) : (
                matches.map(m => (
                  <MatchCard
                    key={`${m.actor_id}-${m.investor_id}`}
                    m={m}
                    onContact={handleContact}
                    onExpand={handleExpand}
                    expanded={expandedKey === `${m.actor_id}-${m.investor_id}`}
                  />
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SupplyDemandMatching;
